import { gql, useApolloClient, useQuery } from '@apollo/client';
import { Alert, Notice, Stack, Strong, Text } from 'braid-design-system';
import React from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import {
  type ClientListing,
  GetListingsTabDocument,
  ListingStatus,
} from 'src/generated/graphql';
import type { ClientListingExpireUpdate } from 'src/hooks/useExpireListing';
import type { DashboardListingFilter } from 'src/types/dashboard';
import {
  BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED,
  getBoostBundlePrices,
} from 'src/utils/feature-flags';
import { formatPriceBand } from 'src/utils/investmentBands';
import { getIsListingLus, getIsListingFree } from 'src/utils/listingProducts';

import { FilteredListings } from './components/FilteredListings';
import { ListingUpgradeContact } from './components/ListingUpgradeContact/ListingUpgradeContact';
import NoListings from './components/NoListings/NoListings';
import TableHeader from './components/TableHeader/TableHeader';

const ListingsTab = () => {
  const apolloClient = useApolloClient();
  const getListingsTabResult = useQuery(GetListingsTabDocument);
  const [rowFilter, setRowFilter] =
    React.useState<DashboardListingFilter>('all');

  if (getListingsTabResult.loading) {
    return <CustomLoader />;
  }

  const listings = getListingsTabResult.data?.user?.userView?.client?.listings;
  if (getListingsTabResult.error || !getListingsTabResult.data || !listings) {
    return (
      <Notice tone="critical">
        <Text>
          There was an issue retrieving your listings. Please try again later.
        </Text>
      </Notice>
    );
  }

  if (listings.length === 0) {
    return <NoListings />;
  }

  const hasActiveListings = listings.some(
    (listing) => listing.status === ListingStatus.Active,
  );

  const activeListing = listings.find(
    (listing) => listing.status === ListingStatus.Active,
  );
  const activeListingIsBoodBundleDiscountEligible =
    activeListing &&
    !activeListing.isPremium &&
    !activeListing.isListingEmailBoosted;

  const isActiveListingLus =
    activeListing !== undefined &&
    getIsListingLus(getListingsTabResult.data.listingProducts, activeListing);

  const isActiveListingFree =
    activeListing &&
    getIsListingFree(getListingsTabResult.data.listingProducts, activeListing);

  const handleListingExpire = (
    id: number,
    listing: ClientListingExpireUpdate,
  ) => {
    const identityObject: Pick<ClientListing, '__typename' | 'id'> = {
      __typename: 'ClientListing',
      id,
    };

    apolloClient.writeFragment({
      id: apolloClient.cache.identify(identityObject),
      fragment: gql`
        fragment ExpireListing on ClientListing {
          autoRenew
          creditRemaining
          expiryDate
          status
        }
      `,
      data: {
        autoRenew: listing.autoRenew,
        creditRemaining: listing.creditRemaining,
        expiryDate: listing.expiryDate,
        status: listing.status,
      },
    });
  };

  return (
    <Stack space="medium">
      {BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED &&
        activeListingIsBoodBundleDiscountEligible && (
          <Alert tone="promote">
            <Text>
              Until 31 March 2025, maximise your ad exposure and save{' '}
              {formatPriceBand(
                getBoostBundlePrices({ isLus: isActiveListingLus }).savings /
                  100,
              )}{' '}
              with Website Search Boost and Email Boost. Select{' '}
              <Strong>Boost</Strong> to learn more.
            </Text>
          </Alert>
        )}

      <Stack space="small">
        <TableHeader
          showCreateAdButton={!hasActiveListings}
          activeFilter={rowFilter}
          onActiveFilterChange={setRowFilter}
        />

        <FilteredListings
          filter={rowFilter}
          listings={listings}
          listingProducts={getListingsTabResult.data.listingProducts}
          onListingExpire={handleListingExpire}
          hasActiveListings={hasActiveListings}
        />
      </Stack>

      {isActiveListingFree && <ListingUpgradeContact />}
    </Stack>
  );
};

export default ListingsTab;
